import { useEffect, useRef, useState } from "react";
import { collection, onSnapshot, query, where, orderBy, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { S, C } from "../ui";
import { uploadFile } from "../core/AssetCheckIn";

function tsToLabel(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// Reusable message thread — Phase 12. Used three ways: the Facility
// Admin's MessagesPanel.jsx (threadType="vendor"|"worker", picking which
// one), VendorAdminDashboard.jsx's own Messages tab (always
// threadType="vendor", threadId=their own vendorId), and StaffHome.jsx's
// Messages tab (always threadType="worker", threadId=their own
// workerId). Each of those three passes a fixed senderRole/senderName for
// "who am I in this conversation" — this component has no identity logic
// of its own, purely "given a thread and who I am, show it and let me
// post to it."
export default function MessageThread({ facilityId, threadType, threadId, senderRole, senderName }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const fileInputRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!threadId) return;
    return onSnapshot(
      query(collection(db, "messages"), where("threadType", "==", threadType), where("threadId", "==", threadId), orderBy("timestamp", "asc")),
      s => setMessages(s.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, [threadType, threadId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ block: "nearest" }); }, [messages.length]);

  const send = async ({ photoUrl } = {}) => {
    if (!text.trim() && !photoUrl) return;
    setSending(true); setErr("");
    try {
      await addDoc(collection(db, "messages"), {
        facilityId, threadType, threadId, senderRole, senderName,
        text: text.trim() || null, photoUrl: photoUrl || null,
        timestamp: serverTimestamp(),
      });
      setText("");
    } catch (e) {
      setErr("Couldn't send — try again.");
    }
    setSending(false);
  };

  const attachPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSending(true); setErr("");
    try {
      const url = await uploadFile(`messagePhotos/${facilityId}/${threadType}_${threadId}/${Date.now()}`, file);
      await send({ photoUrl: url });
    } catch (err2) {
      setErr("Couldn't send photo — try again.");
    }
    setSending(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 420 }}>
      <div style={{ flex: 1, overflowY: "auto", background: C.bg, borderRadius: 0, border: `1px solid ${C.border}`, padding: 16, marginBottom: 12, display: "flex", flexDirection: "column", gap: 10 }}>
        {messages.length === 0 && <div style={{ fontSize: 13, color: C.muted, textAlign: "center", marginTop: 20 }}>No messages yet — say hello.</div>}
        {messages.map(m => {
          const mine = m.senderRole === senderRole;
          return (
            <div key={m.id} style={{ display: "flex", flexDirection: "column", alignItems: mine ? "flex-end" : "flex-start" }}>
              <div style={{ maxWidth: "75%", background: mine ? C.accent : C.white, color: mine ? C.white : C.navy, border: mine ? "none" : `1px solid ${C.border}`, borderRadius: 0, padding: "8px 12px" }}>
                {!mine && <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.7, marginBottom: 2 }}>{m.senderName}</div>}
                {m.text && <div style={{ fontSize: 14, whiteSpace: "pre-wrap" }}>{m.text}</div>}
                {m.photoUrl && <img src={m.photoUrl} alt="" style={{ maxWidth: "100%", borderRadius: 0, marginTop: m.text ? 6 : 0, display: "block" }} />}
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{tsToLabel(m.timestamp)}</div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {err && <div style={S.err}>{err}</div>}
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
        <textarea
          style={{ ...S.input, marginBottom: 0, flex: 1, minHeight: 44, maxHeight: 120, resize: "vertical" }}
          placeholder="Type a message…" value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
        />
        <input ref={fileInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={attachPhoto} />
        <button className="icon-tap-target" style={{ ...S.iconBtn, border: `1.5px solid ${C.border}`, borderRadius: 0, fontSize: 18 }}
          disabled={sending} onClick={() => fileInputRef.current?.click()} title="Attach photo">📷</button>
        <button style={{ ...S.btn(), width: "auto", padding: "12px 18px" }} disabled={sending || !text.trim()} onClick={() => send()}>Send</button>
      </div>
    </div>
  );
}
